import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  cancelDocumentRequest,
  createDocumentRequest,
  fetchDocumentFileDownload,
  fetchLeadDocumentRequests,
  rejectDocumentFile,
  removeDocumentFile,
  reopenDocumentRequest,
  resendDocumentRequest,
  updateDocumentRequest,
  uploadDocumentFile,
  verifyDocumentRequest,
} from './traveller-documents.api';

import type {
  CreateTravellerDocumentRequestInput,
  TravellerDocumentTripScope,
  TravellerDocumentUploadInput,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

const extractMessage = (err: unknown): string | null => extractServerError(err).message;

export const travellerDocumentsQK = {
  leadList: (leadId: string) => ['traveller-documents', 'lead', leadId] as const,
};

/** Customer-driven statuses: poll while the customer may still be uploading. */
const LIVE_STATUSES = new Set(['PENDING', 'PARTIAL']);

export const useLeadDocumentRequests = (leadId: string, enabled = true) =>
  useQuery({
    queryKey: travellerDocumentsQK.leadList(leadId),
    queryFn: () => fetchLeadDocumentRequests(leadId),
    enabled,
    staleTime: 15_000,
    refetchInterval: (query) => {
      const rows = query.state.data;
      const hasLive = Array.isArray(rows) && rows.some((r) => LIVE_STATUSES.has(r.status));
      return hasLive ? 10_000 : false;
    },
    refetchOnWindowFocus: true,
  });

const useInvalidate = (leadId: string) => {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: travellerDocumentsQK.leadList(leadId) });
};

export const useCreateDocumentRequest = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (input: Omit<CreateTravellerDocumentRequestInput, 'leadId'>) =>
      createDocumentRequest({ leadId, ...input }),
    onSuccess: () => {
      invalidate();
      toast.success('Document request sent on WhatsApp + email');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to request documents'),
  });
};

export const useUpdateDocumentRequest = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (args: {
      id: string;
      staffNote?: string | null;
      travellerCount?: number;
      tripScope?: TravellerDocumentTripScope;
    }) => updateDocumentRequest(args.id, args),
    onSuccess: () => {
      invalidate();
      toast.success('Request updated');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to update the request'),
  });
};

export const useResendDocumentRequest = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (id: string) => resendDocumentRequest(id),
    onSuccess: () => {
      invalidate();
      toast.success('Upload link re-sent on WhatsApp + email');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to re-send the link'),
  });
};

/** Staff upload on the customer's behalf. */
export const useUploadDocumentFile = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (args: { id: string; input: TravellerDocumentUploadInput }) =>
      uploadDocumentFile(args.id, args.input),
    onSuccess: () => {
      invalidate();
      toast.success('Document uploaded');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to upload the document'),
  });
};

export const useVerifyDocumentRequest = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (id: string) => verifyDocumentRequest(id),
    onSuccess: () => {
      invalidate();
      toast.success('Documents verified');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to verify'),
  });
};

export const useReopenDocumentRequest = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (id: string) => reopenDocumentRequest(id),
    onSuccess: () => {
      invalidate();
      toast.success('Request reopened - the customer can replace files again');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to reopen'),
  });
};

export const useCancelDocumentRequest = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (id: string) => cancelDocumentRequest(id),
    onSuccess: () => {
      invalidate();
      toast.success('Request cancelled - the link no longer works');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to cancel'),
  });
};

export const useOpenDocumentFile = () =>
  useMutation({
    mutationFn: (fileId: string) => fetchDocumentFileDownload(fileId),
    onSuccess: (res) => {
      window.open(res.url, '_blank', 'noopener');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to open the file'),
  });

/** Save the file: the signed URL carries `attachment`, so navigating to it downloads. */
export const useDownloadDocumentFile = () =>
  useMutation({
    mutationFn: (fileId: string) => fetchDocumentFileDownload(fileId, { attachment: true }),
    onSuccess: (res) => {
      const a = document.createElement('a');
      a.href = res.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to download the file'),
  });

export const useRejectDocumentFile = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (args: { fileId: string; reason: string }) =>
      rejectDocumentFile(args.fileId, args.reason),
    onSuccess: () => {
      invalidate();
      toast.success('File rejected - the customer sees the reason on their page');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to reject the file'),
  });
};

export const useRemoveDocumentFile = (leadId: string) => {
  const invalidate = useInvalidate(leadId);
  return useMutation({
    mutationFn: (fileId: string) => removeDocumentFile(fileId),
    onSuccess: () => {
      invalidate();
      toast.success('File removed');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to remove the file'),
  });
};
